/* eslint-disable no-warning-comments */
// 剧目销售看板 - 接口响应解析工具
// 入参：后端接口返回的 data（已去掉 {code, message, data} 包装层）
// 出参：前端组件期望的扁平结构
//
// 复用 src/utils/reportResponseUtil.js 把 items[] 解析为按 timeDimension / index / valueType 三层结构。
// 7 个接口都对应"多时多指"形态，因此统一调用：
//   - 单值（总览/VIP）：mulTimeDimensionMulIndexSinValue → 取 .ALL
//   - 多值（趋势/漏斗/地域/场次/渠道）：mulTimeDimensionMulIndexMulValue → 取 .ALL/.DAY
import {
  mulTimeDimensionMulIndexMulValue,
  mulTimeDimensionMulIndexSinValue,
} from '@/utils/reportResponseUtil';

const num = (v) => {
  if (v === null || v === undefined || v === '') {
    return null;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// 多值版，取 ALL 层
const byAllMul = (data) => {
  const result = mulTimeDimensionMulIndexMulValue(data?.items || []);
  return result.ALL || {};
};
// 单值版，取 ALL 层
const byAllSingle = (data) => {
  const result = mulTimeDimensionMulIndexSinValue(data?.items || []);
  return result.ALL || {};
};
// 多值版，取 DAY 层（趋势）
const byDayMul = (data) => {
  const result = mulTimeDimensionMulIndexMulValue(data?.items || []);
  return result.DAY || {};
};

// ============== 0. 剧目搜索 ==============
// data 形如：[{ proNo, proName }]
export function parseShowList(data) {
  const list = data?.data || data || [];
  return (Array.isArray(list) ? list : []).map((r) => ({
    proNo: r.proNo,
    proName: r.proName,
  }));
}

// ============== 1. 销售总览 ==============
export function parseOverview(data) {
  const m = byAllSingle(data);
  return {
    dataAsOf: data?.dataAsOf || null,
    seats: num(m.SHOW_TICKET_CNT?.VALUE) || 0,
    buyers: num(m.SHOW_BUYERS?.VALUE) || 0,
    birdAmount: num(m.SHOW_BIRD_AMOUNT?.VALUE) || 0,
    payable: num(m.SHOW_PAYABLE?.VALUE) || 0,
    perCap: num(m.SHOW_TICKET_PER_BUYER?.VALUE) || 0,
    repeatRate: num(m.SHOW_REPEAT_RATE?.VALUE),
  };
}

// ============== 2. VIP ==============
export function parseVip(data) {
  const m = byAllSingle(data);
  return {
    dataAsOf: data?.dataAsOf || null,
    vipBuyers: num(m.SHOW_VIP_BUYERS?.VALUE) || 0,
    vipTickets: num(m.SHOW_VIP_TICKET_CNT?.VALUE) || 0,
    vipBirdAmount: num(m.SHOW_VIP_BIRD_AMOUNT?.VALUE) || 0,
    vipPayable: num(m.SHOW_VIP_PAYABLE?.VALUE) || 0,
    vipBirdRate: num(m.SHOW_VIP_BIRD_RATE?.RATE),
    vipRepeatRate: num(m.SHOW_VIP_REPEAT_RATE?.VALUE),
  };
}

// ============== 3. 访问与销售趋势（DAY 多值） ==============
export function parseVisitSalesTrend(data) {
  const day = byDayMul(data);
  const pvRows = day.SHOW_DETAIL_PV || [];
  const labels = pvRows.map((r) => r.LABEL);
  const labelIndex = new Map(labels.map((l, i) => [l, i]));
  const n = labels.length;

  const fillByLabel = (rows) => {
    const arr = new Array(n).fill(0);
    (rows || []).forEach((r) => {
      const i = labelIndex.get(r.LABEL);
      if (i !== undefined) {
        arr[i] = num(r.VALUE) || 0;
      }
    });
    return arr;
  };

  return {
    dataAsOf: data?.dataAsOf || null,
    visitDataAsOf: data?.visitDataAsOf || null,
    labels,
    pv: fillByLabel(day.SHOW_DETAIL_PV),
    uv: fillByLabel(day.SHOW_DETAIL_UV),
    paid: fillByLabel(day.SHOW_DAILY_PAID),
    earlyBird: fillByLabel(day.SHOW_DAILY_BIRD),
  };
}

// ============== 4. 购票漏斗（ALL 多值） ==============
const FUNNEL_STEPS = [
  { code: 'DETAIL', name: '详情页' },
  { code: 'CALENDAR', name: '日历页' },
  { code: 'SEAT', name: '座位页' },
  { code: 'CONFIRM', name: '订单确认页' },
  { code: 'DEAL', name: '成交' },
];

export function parseFunnel(data) {
  const m = byAllMul(data);
  const uvRows = m.SHOW_FUNNEL_UV || [];
  const stepRows = m.SHOW_FUNNEL_STEP_RATE || [];
  const detailRows = m.SHOW_FUNNEL_DETAIL_RATE || [];

  const uvByCode = {};
  uvRows.forEach((r) => {
    if (r.LABEL) {
      uvByCode[r.LABEL] = num(r.VALUE) || 0;
    }
  });
  const stepByCode = {};
  stepRows.forEach((r) => {
    if (r.LABEL) {
      stepByCode[r.LABEL] = num(r.RATE);
    }
  });
  const detailByCode = {};
  detailRows.forEach((r) => {
    if (r.LABEL) {
      detailByCode[r.LABEL] = num(r.RATE);
    }
  });

  const detailUv = uvByCode.DETAIL || 0;
  const tsjBuyers = uvByCode.DEAL || 0;
  const detailConv = detailUv ? tsjBuyers / detailUv : 0;

  const funnel = FUNNEL_STEPS.map((step) => {
    const nVal = uvByCode[step.code] || 0;
    const prevRate = stepByCode[step.code] || 0;
    const detailRate = detailByCode[step.code] || 0;
    const sharePct = detailRate * 100;
    const wPct = `${sharePct.toFixed(1)}%`;
    return {
      name: step.name,
      n: nVal,
      prev: `${(prevRate * 100).toFixed(1)}%`,
      share: wPct,
      w: wPct,
      light: step.code === 'DEAL',
    };
  });

  return { detailUv, tsjBuyers, detailConv, funnel };
}

// 多值版解析器下，同一 index 可能对应多条记录，统一取第一条
const firstRow = (rows) => (Array.isArray(rows) ? rows[0] : rows) || {};

// ============== 5. 访问地域 TOP5 ==============
export function parseRegionTop(data) {
  const m = byAllMul(data);
  const cityRows = m.SHOW_REGION_CITY || [];

  const geo = cityRows.map((r) => {
    const isOther = r.LABEL === 'OTHER';
    return {
      name: isOther ? '其他地域合计' : r.LABEL,
      pv: num(r.VALUE) || 0,
      p: num(r.RATE) || 0,
      other: isOther,
    };
  });

  return {
    pvTotal: num(firstRow(m.SHOW_REGION_PV_TOTAL).VALUE) || 0,
    localShare: num(firstRow(m.SHOW_REGION_SH_RATE).RATE) || 0,
    outsideShare: num(firstRow(m.SHOW_REGION_OUTER_RATE).RATE) || 0,
    geo,
  };
}

// ============== 6. 分场次销售 ==============
export function parseSessionStack(data) {
  const m = byAllMul(data);
  const soldRows = m.SHOW_SESSION_BAR_SOLD || [];
  const leftRows = m.SHOW_SESSION_BAR_LEFT || [];
  const timeRows = m.SHOW_SESSION_BAR_TIME || [];

  const leftByLabel = {};
  leftRows.forEach((r) => {
    if (r.LABEL) {
      leftByLabel[r.LABEL] = num(r.VALUE) || 0;
    }
  });
  const timeByLabel = {};
  timeRows.forEach((r) => {
    if (r.LABEL) {
      timeByLabel[r.LABEL] = r.VALUE;
    }
  });

  const sessions = soldRows.map((r) => {
    const id = r.LABEL || '';
    const sold = num(r.VALUE) || 0;
    const remain = leftByLabel[id] || 0;
    const time = timeByLabel[id];
    let label = id;
    if (time) {
      // yyyy-MM-dd HH:mm -> MM/dd
      const d = String(time).slice(5, 10).replace('-', '/');
      label = d || id;
    }
    return { id, label, sold, remain, time };
  });

  const totalCount =
    num(firstRow(m.SHOW_SESSION_COUNT).VALUE) || sessions.length;
  const totalSold =
    num(firstRow(m.SHOW_SESSION_SOLD).VALUE) ||
    sessions.reduce((s, x) => s + x.sold, 0);
  const totalRemain =
    num(firstRow(m.SHOW_SESSION_LEFT).VALUE) ||
    sessions.reduce((s, x) => s + x.remain, 0);

  return { totalCount, totalSold, totalRemain, sessions };
}

// ============== 7. 售票渠道 ==============
const CHANNEL_NAME = { TSJ: '台上见', MAOYAN: '猫眼', DAMAI: '大麦' };
const CHANNEL_SOURCE = {
  TSJ: '自有订单库 · 近实时',
  MAOYAN: '文广票池 · T+1',
  DAMAI: '文广票池 · T+1',
};

export function parseChannel(data) {
  const m = byAllMul(data);
  const wentIncluded =
    Boolean(data?.wentIncluded) ||
    num(firstRow(m.SHOW_CHANNEL_WENT_PLAY).VALUE) === 1;

  const ticketRows = m.SHOW_CHANNEL_TICKET || [];
  const paidRows = m.SHOW_CHANNEL_PAID || [];
  const rateRows = m.SHOW_CHANNEL_TICKET_RATE || [];

  const paidByCode = {};
  paidRows.forEach((r) => {
    if (r.LABEL) {
      paidByCode[r.LABEL] = num(r.VALUE) || 0;
    }
  });
  const rateByCode = {};
  rateRows.forEach((r) => {
    if (r.LABEL) {
      rateByCode[r.LABEL] = num(r.RATE) || 0;
    }
  });

  const rows = ticketRows
    .filter((r) => r.LABEL && CHANNEL_NAME[r.LABEL])
    .map((r) => ({
      code: r.LABEL,
      channel: CHANNEL_NAME[r.LABEL],
      tickets: num(r.VALUE) || 0,
      amount: paidByCode[r.LABEL] || 0,
      pct: rateByCode[r.LABEL] || 0,
      source: CHANNEL_SOURCE[r.LABEL],
    }));

  const totalTickets = rows.reduce((s, r) => s + r.tickets, 0);
  const totalAmountWan = rows.reduce((s, r) => s + r.amount, 0);

  return { wentIncluded, rows, totalTickets, totalAmountWan };
}
